import Link from "next/link";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "secondary-dark" | "text-link" | "danger";
type Size = "md" | "sm";

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-primary text-on-primary hover:bg-primary-active disabled:bg-primary-disabled disabled:text-muted",
  secondary:
    "bg-canvas text-ink border border-hairline hover:bg-surface-soft disabled:text-muted-soft",
  "secondary-dark":
    "bg-surface-dark-elevated text-on-dark border border-white/10 hover:bg-surface-dark-soft",
  "text-link": "bg-transparent text-ink underline-offset-4 hover:underline px-0 h-auto",
  danger: "bg-error text-on-primary hover:opacity-90 disabled:opacity-50",
};

const sizeClasses: Record<Size, string> = {
  md: "h-10 px-5 text-sm",
  sm: "h-8 px-3.5 text-xs",
};

const base =
  "inline-flex items-center justify-center gap-2 rounded-md font-body font-medium transition-colors disabled:cursor-not-allowed whitespace-nowrap";

interface CommonProps {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  className,
  children,
  ...props
}: CommonProps & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={cn(base, variant !== "text-link" && sizeClasses[size], variantClasses[variant], className)}
      {...props}
    >
      {children}
    </button>
  );
}

export function LinkButton({
  variant = "primary",
  size = "md",
  className,
  children,
  href,
}: CommonProps & { href: string }) {
  return (
    <Link
      href={href}
      className={cn(base, variant !== "text-link" && sizeClasses[size], variantClasses[variant], className)}
    >
      {children}
    </Link>
  );
}
